import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Componente LightShowMobile (Aficionado)
 * Gestiona la sincronización de flashes y pantallas usando manipulación global del DOM
 * para abarcar el 100% de la pantalla del usuario.
 */
const LightShow = ({ config }) => {
    // ==========================================
    // ESTADOS Y REFERENCIAS
    // ==========================================
    const [hasGrantedPermissions, setHasGrantedPermissions] = useState(false);
    const [permissionError, setPermissionError] = useState(null);

    const videoTrackReference = useRef(null); 
    const wakeLockReference = useRef(null);   
    const flashingIntervalReference = useRef(null); 

    // ==========================================
    // 1. SOLICITUD DE PERMISOS (Cámara y WakeLock)
    // ==========================================
    const handleJoinShowClick = async () => {
        try {
            // Prevenir que la pantalla se apague sola por inactividad
            if ('wakeLock' in navigator) {
                wakeLockReference.current = await navigator.wakeLock.request('screen');
            }
            
            // Pedimos acceso a la cámara trasera para el Flash
            const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            videoTrackReference.current = mediaStream.getVideoTracks()[0];
            
            setHasGrantedPermissions(true);
        } catch (error) {
            console.warn("Permiso de cámara denegado o no disponible.");
            setHasGrantedPermissions(true); // Lo dejamos pasar para que disfrute el show en pantalla
            setPermissionError("Tu flash físico no encenderá, ¡pero tu pantalla sí!");
        }
    };

    // ==========================================
    // 2. CONTROLADOR DE HARDWARE (Flash)
    // ==========================================
    const applyHardwareFlash = useCallback(async (turnOn) => {
        if (videoTrackReference.current) {
            try {
                // Modifica los "constraints" de la cámara para encender la linterna (torch)
                await videoTrackReference.current.applyConstraints({ advanced: [{ torch: turnOn }] });
            } catch (error) { 
                /* Ignorado silenciosamente para evitar bloquear el procesador si el hardware no responde rápido */ 
            }
        }
    }, []);

    // ==========================================
    // 3. LIMPIEZA GLOBAL DEL DOM
    // ==========================================
    const clearGlobalScreenColors = useCallback(() => {
        // Regresamos los contenedores principales a su estado natural (vacío/negro)
        document.body.style.backgroundColor = '';
        
        const rootElement = document.getElementById("root");
        if (rootElement) rootElement.style.backgroundColor = '';
        
        const footerElement = document.getElementById("fdmFooter");
        if (footerElement) footerElement.style.backgroundColor = '';
    }, []);

    // ==========================================
    // 4. EL MOTOR DE SINCRONIZACIÓN (Frames)
    // ==========================================
    useEffect(() => {
        // Si el show no está activo, el usuario no ha dado permisos, o no hay colores configurados: Apagamos todo.
        if (!config?.isRunning || !hasGrantedPermissions || !config?.frames || config.frames.length === 0) {
            applyHardwareFlash(false);
            clearGlobalScreenColors();
            return;
        }

        // Limpieza de seguridad antes de arrancar
        if (flashingIntervalReference.current) clearInterval(flashingIntervalReference.current);

        const beatsPerMinute = config.beatsPerMinute || 120;
        
        // Calculamos cuánto dura cada "Frame" en milisegundos.
        // Ej: A 120 BPM, un latido dura 500ms. Si hay 2 frames, cada frame dura 250ms.
        const millisecondsPerFrame = (60000 / beatsPerMinute) / (config.frames.length || 2); 
        
        // El reloj maestro (La batuta del director)
        const serverStartTimeAnchor = config.serverStartTimestampMilliseconds || Date.now();

        // Memoria local del bucle para no saturar hardware ni DOM
        let lastFrameColorApplied = null;
        let lastFrameFlashApplied = null;

        // Bucle de alta velocidad (aprox. 50 FPS)
        flashingIntervalReference.current = setInterval(() => {
            const timeElapsedSinceStart = Date.now() - serverStartTimeAnchor;
            const absoluteFrameCount = Math.floor(timeElapsedSinceStart / millisecondsPerFrame);
            
            // Evaluamos qué frame toca mostrar en este instante
            const currentFrameIndexInArray = absoluteFrameCount % config.frames.length;
            const activeFrame = config.frames[currentFrameIndexInArray];
            
            // A) ACTUALIZACIÓN DE PANTALLA (GLOBAL)
            if (lastFrameColorApplied !== activeFrame.color) {
                lastFrameColorApplied = activeFrame.color;
                
                // Pintamos los contenedores padre directamente
                document.body.style.backgroundColor = activeFrame.color;
                
                const rootElement = document.getElementById("root");
                if (rootElement) rootElement.style.backgroundColor = activeFrame.color;
                
                const footerElement = document.getElementById("fdmFooter");
                if (footerElement) footerElement.style.backgroundColor = activeFrame.color;
            }

            // B) ACTUALIZACIÓN DE HARDWARE (FLASH)
            if (lastFrameFlashApplied !== activeFrame.flashOn) {
                lastFrameFlashApplied = activeFrame.flashOn;
                applyHardwareFlash(activeFrame.flashOn);
            }

        }, 20); 

        // Función de limpieza cuando el componente se desmonta o el show se detiene
        return () => {
            if (flashingIntervalReference.current) clearInterval(flashingIntervalReference.current);
            applyHardwareFlash(false);
            clearGlobalScreenColors();
        };
    }, [config?.isRunning, hasGrantedPermissions, config, applyHardwareFlash, clearGlobalScreenColors]);

    
    // ==========================================
    // 5. RENDERIZADO DEL COMPONENTE
    // ==========================================
    return (
        <div 
            style={{ 
                // height: '100dvh', 
                // width: '100vw', 
                backgroundColor: 'transparent', // Transparente para dejar ver el document.body modificado
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center', 
                alignItems: 'center',
                // color: 'white', 
                padding: '20px', 
                textAlign: 'center',
                touchAction: 'none', // Previene el zoom accidental (Doble Tap)
                userSelect: 'none'   // Previene seleccionar texto al dejar el dedo presionado
            }}
        >
            {/* VISTA 1: SOLICITUD DE PERMISOS */}
            {!hasGrantedPermissions && (
                // <div className="p-4 shadow-lg" style={{ backgroundColor: 'rgba(20, 20, 20, 0.95)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', maxWidth: '400px', backdropFilter: 'blur(10px)' }}>
                <div className="p-4" style={{ maxWidth: '400px', backdropFilter: 'blur(10px)' }}>
                    <i className="bi bi-phone-vibrate text-warning" style={{ fontSize: '5rem' }}></i>
                    {/* <h2 className="font-weight-bold mt-3 mb-2 text-white">Show de Luces</h2> */}
                    <p className="text-success mb-0 fs-4" style={{ fontSize: '1.1rem' }}>¡El estadio está a punto de brillar!</p>
                    <p className="mb-4" style={{ fontSize: '1.1rem' }}>Para unirte, necesitamos tu permiso para usar la linterna de tu smartphone.</p>
                    
                    <button className="Btn__Adsl--Red" style={{ backgroundColor: '#038244' }} onClick={handleJoinShowClick}>Aceptar permiso para unirme</button>
                    <p className="small mt-3 mb-0">
                        <i className="bi bi-shield-lock me-1"></i> Solo usamos tu linterna, no grabamos nada.
                    </p>
                </div>
            )}

            {/* VISTA 2: PERMISOS OTORGADOS, EN ESPERA DEL ADMIN */}
            {hasGrantedPermissions && !config?.isRunning && (
                <div className="p-4" style={{ backdropFilter: 'blur(5px)' }}>
                    <i className="bi bi-check-circle-fill text-success mb-3" style={{ fontSize: '4.5rem' }}></i>
                    <h2 className="font-weight-bold mb-3">¡Estás conectad@!</h2>
                    <p className="fs-5">El show está a punto de comenzar...</p>
                    <p className="mb-0 text-danger small">No salgas de esta pantalla</p>
                    {/* <p className="text-warning font-weight-bold mt-2 mb-0">
                        El show comenzará en cualquier momento...
                    </p> */}
                    {permissionError && (
                        <div className="alert alert-warning mt-4 small mb-0 text-start">
                            <i className="bi bi-info-circle-fill me-1"></i> {permissionError}
                        </div>
                    )}
                </div>
            )}

            {/* VISTA 3: SHOW CORRIENDO */}
            {hasGrantedPermissions && config?.isRunning && (
                // No mostramos nada. La pantalla entera se tiñe del color dictado por el motor de Frames.
                // <></>
                <div className="p-4" style={{ backdropFilter: 'blur(5px)' }}>
                    <h2 className="font-weight-bold mb-3">¡Disfruta el show!</h2>
                    <p className="mb-0 text-danger small">No salgas de esta pantalla</p>
                </div>
            )}
        </div>
    );
};

export { LightShow };









// Funcional
// import React, { useState, useEffect, useRef, useCallback } from 'react';


// const LightShow = ({ config }) => {
//     const [hasGrantedPermissions, setHasGrantedPermissions] = useState(false);
//     const [permissionError, setPermissionError] = useState(null);

//     // NUEVO: Usamos useRef en lugar de useState para el color, 
//     // así evitamos que React colapse al intentar re-dibujar 50 veces por segundo.
//     const containerReference = useRef(null); 
    
//     const videoTrackReference = useRef(null); 
//     const wakeLockReference = useRef(null);   
//     const flashingIntervalReference = useRef(null); 

//     const handleJoinShowClick = async () => {
//         try {
//             if ('wakeLock' in navigator) {
//                 wakeLockReference.current = await navigator.wakeLock.request('screen');
//             }
//             const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
//             videoTrackReference.current = mediaStream.getVideoTracks()[0];
//             setHasGrantedPermissions(true);
//         } catch (error) {
//             setHasGrantedPermissions(true); 
//             setPermissionError("Tu flash físico no encenderá, ¡pero tu pantalla sí!");
//         }
//     };

//     const applyHardwareFlash = useCallback(async (turnOn) => {
//         if (videoTrackReference.current) {
//             try {
//                 await videoTrackReference.current.applyConstraints({ advanced: [{ torch: turnOn }] });
//             } catch (error) { /* Ignorado silenciosamente para evitar trabar el hardware */ }
//         }
//     }, []);

//     // MOTOR DE FRAMES Y SINCRONIZACIÓN
//     useEffect(() => {
//         if (!config?.isRunning || !hasGrantedPermissions || !config?.frames || config.frames.length === 0) {
//             applyHardwareFlash(false);
//             if (containerReference.current) containerReference.current.style.backgroundColor = '#000000';
//             return;
//         }

//         if (flashingIntervalReference.current) clearInterval(flashingIntervalReference.current);

//         const beatsPerMinute = config.beatsPerMinute || 120;
//         const millisecondsPerFrame = (60000 / beatsPerMinute) / (config.frames.length || 2); 
//         const serverStartTime = config.serverStartTimestampMilliseconds || Date.now();

//         // Variables de memoria locales para no saturar el hardware
//         let lastFrameColor = null;
//         let lastFrameFlash = null;

//         flashingIntervalReference.current = setInterval(() => {
//             const timeElapsedSinceStart = Date.now() - serverStartTime;
//             const absoluteFrameCount = Math.floor(timeElapsedSinceStart / millisecondsPerFrame);
            
//             const currentFrameIndexInArray = absoluteFrameCount % config.frames.length;
//             const activeFrame = config.frames[currentFrameIndexInArray];
            
//             // 1. SOLO cambiamos el color si es diferente al anterior (Bypass de React Render)
//             if (lastFrameColor !== activeFrame.color) {
//                 lastFrameColor = activeFrame.color;
//                 if (containerReference.current) {
//                     containerReference.current.style.backgroundColor = activeFrame.color;
//                 }
//             }

//             // 2. SOLO le mandamos la orden a la cámara si el flash cambió de estado
//             if (lastFrameFlash !== activeFrame.flashOn) {
//                 lastFrameFlash = activeFrame.flashOn;
//                 applyHardwareFlash(activeFrame.flashOn);
//             }

//         }, 20); // El motor corre a 50 FPS, pero solo ejecuta acciones cuando hay cambios reales.

//         return () => {
//             if (flashingIntervalReference.current) clearInterval(flashingIntervalReference.current);
//             applyHardwareFlash(false);
//             if (containerReference.current) containerReference.current.style.backgroundColor = '#000000';
//         };
//     }, [config?.isRunning, hasGrantedPermissions, config, applyHardwareFlash]);


//     return (
//         <div 
//             ref={containerReference} // Conectamos el div directo al DOM
//             style={{ 
//                 height: '100dvh', width: '100vw', 
//                 backgroundColor: '#000000', // Inicia negro por defecto
//                 display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
//                 color: 'white', padding: '20px', textAlign: 'center',
//                 transition: 'background-color 0.05s linear', 
//                 touchAction: 'none', userSelect: 'none'   
//             }}
//         >
//             {!hasGrantedPermissions && (
//                 <div className="p-4" style={{ backgroundColor: 'rgba(30, 30, 30, 0.8)', borderRadius: '20px', border: '1px solid #444', maxWidth: '400px' }}>
//                     <i className="bi bi-phone-vibrate text-warning" style={{ fontSize: '4rem' }}></i>
//                     <h2 className="font-weight-bold mt-3 mb-2 text-white">Show de Luces</h2>
//                     <p className="text-light mb-4">Para que tu celular brille, necesitamos tu permiso.</p>
//                     <button className="Btn__Adsl--Red w-100 py-3 font-weight-bold" onClick={handleJoinShowClick}>¡UNIRME AL SHOW!</button>
//                 </div>
//             )}

//             {hasGrantedPermissions && !config?.isRunning && (
//                 <div className="p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', borderRadius: '20px' }}>
//                     <i className="bi bi-check-circle-fill text-success mb-3" style={{ fontSize: '4rem' }}></i>
//                     <h2 className="font-weight-bold text-white">¡Estás conectad@!</h2>
//                     <p className="text-light mt-3 mb-0">Apunta tu celular hacia el campo.<br/>El show comenzará en cualquier momento...</p>
//                     {permissionError && <div className="alert alert-warning mt-4 small">{permissionError}</div>}
//                 </div>
//             )}
//         </div>
//     );
// };

// export { LightShow };









// import React, { useState, useEffect, useRef, useCallback } from 'react';


// const LightShow = ({ config }) => {
//     const [hasGrantedPermissions, setHasGrantedPermissions] = useState(false);
//     const [permissionError, setPermissionError] = useState(null);
//     const [currentScreenColor, setCurrentScreenColor] = useState('#000000'); 

//     const videoTrackReference = useRef(null); 
//     const wakeLockReference = useRef(null);   
//     const flashingIntervalReference = useRef(null); 

//     const handleJoinShowClick = async () => {
//         try {
//             if ('wakeLock' in navigator) {
//                 wakeLockReference.current = await navigator.wakeLock.request('screen');
//             }
//             const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
//             videoTrackReference.current = mediaStream.getVideoTracks()[0];
//             setHasGrantedPermissions(true);
//         } catch (error) {
//             setHasGrantedPermissions(true); 
//             setPermissionError("Tu flash físico no encenderá, ¡pero tu pantalla sí!");
//         }
//     };

//     const applyHardwareFlash = useCallback(async (turnOn) => {
//         if (videoTrackReference.current) {
//             try {
//                 await videoTrackReference.current.applyConstraints({ advanced: [{ torch: turnOn }] });
//             } catch (error) { /* Ignorado silenciosamente para evitar trabar el hardware */ }
//         }
//     }, []);

//     // MOTOR DE FRAMES Y SINCRONIZACIÓN
//     useEffect(() => {
//         if (!config?.isRunning || !hasGrantedPermissions || !config?.frames || config.frames.length === 0) {
//             // Si el juego se detiene (Stop), apagamos todo inmediatamente
//             applyHardwareFlash(false);
//             setCurrentScreenColor('#000000');
//             return;
//         }

//         if (flashingIntervalReference.current) clearInterval(flashingIntervalReference.current);

//         const beatsPerMinute = config.beatsPerMinute || 120;
        
//         // Cada latido (beat) se divide en 2 frames (ej. Prende y Apaga)
//         const millisecondsPerFrame = (60000 / beatsPerMinute) / 2; 
        
//         // Usamos el Timestamp que mandó el servidor para que todos los celulares estén en la misma línea de tiempo
//         const serverStartTime = config.serverStartTimestampMilliseconds || Date.now();

//         flashingIntervalReference.current = setInterval(() => {
//             const timeElapsedSinceStart = Date.now() - serverStartTime;
            
//             // Calculamos en qué número de frame exacto vamos desde que inició el show
//             const absoluteFrameCount = Math.floor(timeElapsedSinceStart / millisecondsPerFrame);
            
//             // Ciclamos a través de la lista de frames que mandó el Admin
//             const currentFrameIndexInArray = absoluteFrameCount % config.frames.length;
//             const activeFrame = config.frames[currentFrameIndexInArray];
            
//             // Aplicamos los valores de este frame
//             applyHardwareFlash(activeFrame.flashOn);
//             setCurrentScreenColor(activeFrame.color);

//         }, 20); // Refresco de alta velocidad (50 FPS)

//         return () => {
//             if (flashingIntervalReference.current) clearInterval(flashingIntervalReference.current);
//             applyHardwareFlash(false);
//             setCurrentScreenColor('#000000');
//         };
//     }, [config?.isRunning, hasGrantedPermissions, config, applyHardwareFlash]);

//     return (
//         <div 
//             style={{ 
//                 height: '100dvh', width: '100vw', 
//                 backgroundColor: currentScreenColor, // El color dinámico del Frame
//                 display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
//                 color: 'white', padding: '20px', textAlign: 'center',
//                 transition: 'background-color 0.05s linear', 
//                 touchAction: 'none', userSelect: 'none'   
//             }}
//         >
//             {!hasGrantedPermissions && (
//                 <div className="p-4" style={{ backgroundColor: 'rgba(30, 30, 30, 0.8)', borderRadius: '20px', border: '1px solid #444', maxWidth: '400px' }}>
//                     <i className="bi bi-phone-vibrate text-warning" style={{ fontSize: '4rem' }}></i>
//                     <h2 className="font-weight-bold mt-3 mb-2 text-white">Show de Luces</h2>
//                     <p className="text-light mb-4">Para que tu celular brille, necesitamos tu permiso.</p>
//                     <button className="Btn__Adsl--Red w-100 py-3 font-weight-bold" onClick={handleJoinShowClick}>¡UNIRME AL SHOW!</button>
//                 </div>
//             )}

//             {hasGrantedPermissions && !config?.isRunning && (
//                 <div className="p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', borderRadius: '20px' }}>
//                     <i className="bi bi-check-circle-fill text-success mb-3" style={{ fontSize: '4rem' }}></i>
//                     <h2 className="font-weight-bold text-white">¡Estás conectad@!</h2>
//                     <p className="text-light mt-3 mb-0">Apunta tu celular hacia el campo.<br/>El show comenzará en cualquier momento...</p>
//                     {permissionError && <div className="alert alert-warning mt-4 small">{permissionError}</div>}
//                 </div>
//             )}
//         </div>
//     );
// };

// export { LightShow };









// import React, { useState, useEffect, useRef, useCallback } from 'react';

// /**
//  * Componente LightShow (Aficionado)
//  * Recibe la orden de prepararse, pide permisos de linterna al usuario, 
//  * y ejecuta el parpadeo sincronizado cuando el Admin da la orden de inicio.
//  */
// const LightShow = ({ config }) => {
//     // --- ESTADOS DEL USUARIO ---
//     const [hasGrantedPermissions, setHasGrantedPermissions] = useState(false);
//     const [permissionError, setPermissionError] = useState(null);
//     const [screenFlashState, setScreenFlashState] = useState(false); // Controla el parpadeo visual en pantalla

//     // --- REFERENCIAS DE HARDWARE Y TIMERS ---
//     const videoTrackReference = useRef(null); // Guarda el acceso a la cámara
//     const wakeLockReference = useRef(null);   // Previene que la pantalla se apague
//     const flashingIntervalReference = useRef(null); // El bucle del parpadeo

//     // =========================================================
//     // 1. SOLICITUD DE PERMISOS (Cámara y WakeLock)
//     // =========================================================
//     const handleJoinShowClick = async () => {
//         try {
//             // A) PREVENIR QUE SE APAGUE LA PANTALLA (Wake Lock API)
//             if ('wakeLock' in navigator) {
//                 wakeLockReference.current = await navigator.wakeLock.request('screen');
//             }

//             // B) SOLICITAR CÁMARA PARA USAR EL FLASH (Torch)
//             // Solo pedimos la cámara trasera ('environment')
//             const mediaStream = await navigator.mediaDevices.getUserMedia({
//                 video: { facingMode: 'environment' }
//             });
            
//             // Extraemos el "track" de video (la cámara como tal)
//             const videoTrack = mediaStream.getVideoTracks()[0];
//             videoTrackReference.current = videoTrack;

//             // Verificamos si este celular en específico tiene linterna (Flash)
//             const trackCapabilities = videoTrack.getCapabilities();
//             if (!trackCapabilities.torch) {
//                 console.warn("Este dispositivo no soporta el control de la linterna vía Web.");
//                 // No lanzamos error porque la pantalla SÍ va a parpadear, el show continúa.
//             }

//             // Si llegamos aquí, el usuario dio permiso
//             setHasGrantedPermissions(true);

//         } catch (error) {
//             console.error("Error al solicitar permisos:", error);
//             // Si el usuario deniega la cámara, igual lo dejamos pasar para que vea 
//             // su pantalla parpadear, solo le avisamos que el flash físico no funcionará.
//             setHasGrantedPermissions(true); 
//             setPermissionError("Tu flash físico no encenderá, ¡pero tu pantalla sí!");
//         }
//     };

//     // =========================================================
//     // 2. CONTROL DEL HARDWARE (Encender/Apagar Linterna)
//     // =========================================================
//     const applyHardwareFlash = useCallback(async (turnOn) => {
//         // 1. Parpadeo de la Pantalla (React State)
//         setScreenFlashState(turnOn);

//         // 2. Parpadeo Físico (Linterna del Celular)
//         if (videoTrackReference.current) {
//             try {
//                 await videoTrackReference.current.applyConstraints({
//                     advanced: [{ torch: turnOn }]
//                 });
//             } catch (error) {
//                 // Se ignora silenciosamente. Algunos celulares se traban si se les pide 
//                 // prender/apagar el hardware muy rápido.
//             }
//         }
//     }, []);

//     // =========================================================
//     // 3. EL MOTOR DE SINCRONIZACIÓN (El corazón del show)
//     // =========================================================
//     useEffect(() => {
//         // Si el Admin aún no presiona "Iniciar", o si el usuario no ha dado permisos, no hacemos nada.
//         if (!config?.isRunning || !hasGrantedPermissions) return;

//         // Limpiamos cualquier bucle anterior por seguridad
//         if (flashingIntervalReference.current) clearInterval(flashingIntervalReference.current);

//         // Matemáticas del Ritmo:
//         // BPM = Beats Per Minute (Ej. 120 latidos en 60,000 milisegundos)
//         const beatsPerMinute = config.beatsPerMinute || 120;
//         const millisecondsPerBeat = 60000 / beatsPerMinute;
        
//         // MARCA DE TIEMPO CERO: El momento exacto en que este bloque de código se ejecuta 
//         // (Justo cuando llega el mensaje de WebSocket del servidor)
//         const localStartTimeAnchor = Date.now();

//         // Iniciamos un bucle ultrarrápido (aprox. 30 FPS)
//         flashingIntervalReference.current = setInterval(() => {
//             // ¿Cuánto tiempo ha pasado desde que arrancó el show?
//             const timeElapsed = Date.now() - localStartTimeAnchor;
            
//             // ¿En qué parte del "latido" estamos? (Fase del ciclo)
//             const currentPhase = timeElapsed % millisecondsPerBeat;
            
//             // Decidimos si la luz debe estar prendida o apagada
//             // Ejemplo: si el latido dura 500ms, los primeros 250ms está prendida, los otros 250ms apagada (50% del tiempo)
//             // Si quieres que el destello sea más corto (tipo estrobo), cambia el 0.5 por 0.2
//             const shouldBeLightOn = currentPhase < (millisecondsPerBeat * 0.5);
            
//             // Mandamos la orden al hardware y a la pantalla
//             applyHardwareFlash(shouldBeLightOn);

//         }, 33); // 33ms = ~30 cuadros por segundo de refresco

//         // =========================================================
//         // LIMPIEZA AL DETENER EL SHOW O DESMONTAR EL COMPONENTE
//         // =========================================================
//         return () => {
//             if (flashingIntervalReference.current) {
//                 clearInterval(flashingIntervalReference.current);
//             }
            
//             // Apagamos la luz por si se quedó prendida en el último milisegundo
//             applyHardwareFlash(false);
            
//             // Liberamos la cámara
//             if (videoTrackReference.current) {
//                 videoTrackReference.current.stop();
//                 videoTrackReference.current = null;
//             }

//             // Liberamos el bloqueo de pantalla
//             if (wakeLockReference.current) {
//                 wakeLockReference.current.release();
//                 wakeLockReference.current = null;
//             }
//         };
//     }, [config?.isRunning, hasGrantedPermissions, applyHardwareFlash, config?.beatsPerMinute]);


//     // =========================================================
//     // RENDERIZADO DEL CELULAR
//     // =========================================================

//     // Calculamos el color de fondo:
//     // Si el show está corriendo Y es momento del destello -> Usamos el color del Admin
//     // En cualquier otro caso -> Negro absoluto
//     const dynamicBackgroundColor = (config?.isRunning && screenFlashState) 
//         ? (config?.themeColor || '#ffffff') 
//         : '#000000';

//     return (
//         <div 
//             style={{ 
//                 height: '100dvh', 
//                 width: '100vw', 
//                 backgroundColor: dynamicBackgroundColor,
//                 display: 'flex',
//                 flexDirection: 'column',
//                 justifyContent: 'center',
//                 alignItems: 'center',
//                 color: 'white',
//                 padding: '20px',
//                 textAlign: 'center',
//                 transition: 'background-color 0.05s ease-in-out', // Transición rápida para que no se vea tosco
//                 touchAction: 'none', // Previene el zoom accidental
//                 userSelect: 'none'   // Previene seleccionar texto por error
//             }}
//         >
//             {/* ESCENARIO 1: EL USUARIO AÚN NO DA PERMISOS */}
//             {!hasGrantedPermissions && (
//                 <div className="p-4" style={{ backgroundColor: 'rgba(30, 30, 30, 0.8)', borderRadius: '20px', border: '1px solid #444', maxWidth: '400px' }}>
//                     <i className="bi bi-phone-vibrate text-warning" style={{ fontSize: '4rem' }}></i>
//                     <h2 className="font-weight-bold mt-3 mb-2 text-white">Show de Luces</h2>
//                     <p className="text-light mb-4">
//                         Estamos a punto de sincronizar el estadio. Para que tu celular brille, necesitamos tu permiso.
//                     </p>
//                     <button 
//                         className="Btn__Adsl--Red w-100 py-3 font-weight-bold" 
//                         style={{ fontSize: '1.2rem', borderRadius: '50px' }}
//                         onClick={handleJoinShowClick}
//                     >
//                         ¡UNIRME AL SHOW!
//                     </button>
//                     <p className="small text-muted mt-3 mb-0">
//                         <i className="bi bi-shield-lock me-1"></i> No tomamos fotos ni grabamos video.
//                     </p>
//                 </div>
//             )}

//             {/* ESCENARIO 2: YA DIO PERMISO, PERO EL ADMIN AÚN NO INICIA */}
//             {hasGrantedPermissions && !config?.isRunning && (
//                 <div>
//                     <i className="bi bi-check-circle-fill text-success mb-3" style={{ fontSize: '4rem' }}></i>
//                     <h2 className="font-weight-bold text-white">¡Estás conectad@!</h2>
//                     <p className="text-light fs-5 mt-3">
//                         Apunta tu celular hacia el campo.<br/>
//                         El show comenzará en cualquier momento...
//                     </p>
                    
//                     {permissionError && (
//                         <div className="alert alert-warning mt-4 small">
//                             <i className="bi bi-info-circle-fill me-1"></i> {permissionError}
//                         </div>
//                     )}
//                 </div>
//             )}

//             {/* ESCENARIO 3: SHOW EN CURSO */}
//             {hasGrantedPermissions && config?.isRunning && (
//                 // En este estado no mostramos texto para que toda la pantalla sea puro color de luz
//                 // Solo un ícono fantasma en el fondo por estética
//                 <i className="bi bi-lightning-charge-fill" style={{ fontSize: '10rem', opacity: 0.1 }}></i>
//             )}
//         </div>
//     );
// };

// export { LightShow };









// import { useEffect } from 'react'


// const LightShow = () => {

//     const LoadPage = async () => {
//         try {
//             document.getElementById("root").classList.add("BodyComplete")
//         } catch (error) {
            
//         }
//     }

//     useEffect(() => {
//         LoadPage()
//     },[])


//     return (
//         <div className="Colors">
//             {/* <p className="Colors__Icon Colors__Icon--LowOpacity"><i className="bi bi-phone-flip"></i></p> */}
//             <p className="Colors__Title Colors__Title--LowOpacity">¡Gracias por unirte!</p>
//             <p className="Colors__Title Colors__Title--LowOpacity">Disfruta el espectáculo de luces</p>
//         </div>
//     )
// }

// export { LightShow };